// src/pages/HowToUse.jsx
import { useNavigate } from "react-router-dom";

export default function HowToUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-16 space-y-10">
        {/* Heading */}
        <header className="text-center space-y-3">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">
            How to Use
          </h1>
          <p className="text-gray-600">
            A quick guide to starting, typing with gestures, and stopping safely.
          </p>
        </header>

        {/* Step 1 */}
        <section className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-5">
          <h2 className="text-2xl font-semibold">Step 1: Start the Preview</h2>
          <p className="text-gray-700">
            Go to <span className="font-medium">Preview</span> and click{" "}
            <span className="font-semibold">Start Preview</span>. Your webcam
            view appears with the virtual keyboard overlay.
          </p>

          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <img
              src="/preview_img.png"
              alt="Preview screen showing virtual keyboard with webcam overlay"
              className="w-full h-auto"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-900">
              If your browser asks for camera access, click{" "}
              <strong>Allow</strong>. Good lighting improves tracking accuracy.
            </p>
          </div>
        </section>

        {/* Step 2 */}
{/* Step 2 */}
<section className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-5">
  <h2 className="text-2xl font-semibold">Step 2: Type with Hand Gestures</h2>
  <p className="text-gray-700">
    Move your index finger over a key to hover it. To “click” a key,
    bring your <strong>index and middle finger together</strong> (a pinch).
    That triggers a key press on the highlighted key.
  </p>

  <div className="relative w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm flex justify-center">
    <img
      src="/gesture_guide.jpg"
      alt="Hand gesture guide showing index and middle finger pinch to press keys"
      className="max-h-72 w-auto object-contain"
    />
  </div>

  <ul className="list-disc list-inside text-gray-700 space-y-2">
    <li>Hover = move index finger over a key.</li>
    <li>Click = pinch index + middle finger.</li>
    <li>
      Backspace, Enter, Space, Arrows, etc. work like a normal keyboard.
    </li>
  </ul>
</section>

        {/* Step 3 */}
        <section className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-5">
          <h2 className="text-2xl font-semibold">Step 3: Modifiers & Toggles</h2>
          <p className="text-gray-700">Some keys toggle on/off when tapped once:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>
              <strong>Shift / Alt / Win / Fn / Caps Lock</strong>: tap once to
              enable (you’ll see a visual state), tap again to disable.
            </li>
            <li>
              Use <strong>Tab</strong> for indentation, <strong>Enter</strong> for a new line,
              and <strong>Backspace</strong> to delete.
            </li>
          </ul>
        </section>

        {/* Step 4 */}
        <section className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-5">
          <h2 className="text-2xl font-semibold">Step 4: Stop & Exit</h2>
          <div className="space-y-3 text-gray-700">
            <p>
              <strong>Quick stop:</strong> Tap the <strong>ESC</strong> key on the
              virtual keyboard. This sends an ESC command to stop the session.
            </p>
            <p>
              <strong>Preview only:</strong> Click <strong>Stop Preview</strong> to turn
              off the camera and pause hand tracking.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-yellow-900">
              If the preview is paused but you still see the page, just click{" "}
              <strong>Start Preview</strong> again to resume.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="flex items-center justify-center pt-4">
          <button
            aria-label="Open Preview Page"
            onClick={() => navigate("/keyboard")}
            className="px-6 py-3 text-lg rounded-xl bg-gray-900 text-white shadow-md hover:bg-black transition"
          >
            Open Preview
          </button>
        </div>
      </div>
    </div>
  );
}
